import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "success";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  primary: "bg-accent-gradient text-white shadow-glow hover:brightness-110 hover:-translate-y-px disabled:opacity-50 disabled:shadow-none disabled:translate-y-0",
  secondary: "surface text-default border border-white/70 shadow-sm hover:bg-white/90 disabled:opacity-50",
  danger: "bg-danger-gradient text-white shadow-[0_10px_30px_-12px_rgb(239_68_68/0.6)] hover:brightness-110 disabled:opacity-50",
  success: "bg-success-gradient text-white shadow-[0_10px_30px_-12px_rgb(16_185_129/0.6)] hover:brightness-110 disabled:opacity-50",
  ghost: "text-muted hover:bg-white/60 disabled:opacity-50",
};
const sizes: Record<Size, string> = { sm: "h-8 px-3 text-sm", md: "h-10 px-4 text-sm", lg: "h-12 px-6 text-base" };

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading = false, className = "", disabled, children, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`focus-ring inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-200 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});
